import { EventMeta } from '../../../interfaces/meta.interface';

type UnifiedGroupLinksDescFields = {
    unifiedGroupId: string;
    recipientIds: string;
    linkType: string;
};

const UnifiedGroupLinks: EventMeta<UnifiedGroupLinksDescFields> = {
    title: 'Unified Group Links Added',
    default: {
        description: 'Unified Group Links Added',
        longDescription: 'Links were added to a unified group.',
        severity: 'info',
    },
    descriptionExtractor: (log: any) => ({
        unifiedGroupId: log.unifiedGroupId,
        recipientIds: log.recipientIds,
        linkType: log.linkType,
    }),
    classifications: [
        {
            severity: 'critical',
            description: (fields) => `A suspicious IP attempted to add members to a Microsoft 365 Group`,
            longDescription: (fields) =>
                `Members were added to a Microsoft 365 Group from a suspicious IP address that is not in your allowed list. This activity could indicate an attempt to manipulate group membership or gain unauthorized access to resources.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'danger',
            description: (fields) => `An unapproved IP attempted to add members to a Microsoft 365 Group`,
            longDescription: (fields) =>
                `Members were added to a Microsoft 365 Group from an IP address that is not in your allowed list. This activity may indicate an attempt to manipulate group membership or gain unauthorized access to resources.`,
            predicates: {
                isAllowedASN: false,
                isAllowedCountry: false,
                isAllowedRegion: false,
                isAllowedIP: false,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'warning',
            description: (fields) => `A suspicious IP added members to a Microsoft 365 group`,
            longDescription: (fields) =>
                `Members were added to a Microsoft 365 group. The action originated from a suspicious IP. Group membership changes are often part of normal collaboration, but the source IP being flagged as suspicious makes this event noteworthy. This entry is informational — it does not indicate immediate risk, but monitoring for repeated or unusual group changes from suspicious IPs can help identify unwanted access attempts.`,
            predicates: {
                isFraudulentIP: true,
                isMicrosoftActivity: false,
            },
        },
        {
            severity: 'info',
            description: (fields) =>
                `A Microsoft system operation (synchronization server IP) added members to a Microsoft 365 group`,
            longDescription: (fields) =>
                `Members were added to a Microsoft 365 group. This was recorded as a Microsoft system operation event, with the IP address belonging to the Microsoft server responsible for synchronization. This entry is informational and reflects expected background activity. No action is required unless group membership changes appear inconsistent with your configuration or policy.`,
            predicates: {
                isMicrosoftActivity: true,
            },
        },
        {
            severity: 'info',
            description: (fields) => `Members were added to a Microsoft 365 group`,
            longDescription: (fields) =>
                `Members were added to a Microsoft 365 group. This is an informational log of a standard membership change. No unusual conditions were noted in this event. It does not require action unless the addition of members to the group was unexpected.`,
        },
    ],
};

export default UnifiedGroupLinks;
